import { siteUrlFromDomainName } from './panel-user';

export function parseUrlList(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
}

/** Merge URL lists (unique, order preserved); optional cap for request limits. */
export function mergeUrlLists(
  existingUrls: string[],
  newUrls: string[],
  limit?: number,
): string[] {
  const merged = [...new Set([...existingUrls, ...newUrls])];
  return limit !== undefined ? merged.slice(0, limit) : merged;
}

export function mergeUrlListText(
  existingText: string,
  newUrls: string[],
  limit?: number,
): string {
  return mergeUrlLists(parseUrlList(existingText), newUrls, limit).join('\n');
}

/** Lines repeated in the textarea (after trim); not counted in `parseUrlList`. */
export function countUrlListDuplicates(text: string): number {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return Math.max(0, lines.length - new Set(lines).size);
}

export function listUrlListDuplicateLines(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const line of lines) {
    if (seen.has(line)) duplicates.add(line);
    else seen.add(line);
  }
  return [...duplicates];
}

export function removeUrlsFromText(text: string, urls: string[]): string {
  const drop = new Set(urls);
  return text
    .split(/\r?\n/)
    .filter((line) => !drop.has(line.trim()))
    .join('\n');
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Decode percent-encoded URLs for readable UI (e.g. Persian path segments).
 * Safe for malformed input; returns the original string on failure.
 */
export function decodeUrlForDisplay(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return url;
  try {
    let current = trimmed;
    for (let i = 0; i < 3; i++) {
      const next = decodeURI(current);
      if (next === current) break;
      current = next;
    }
    return current;
  } catch {
    return trimmed;
  }
}

const PROJECT_STORAGE = 'indexflow.projectId';

export function loadSelectedProjectId(): string | null {
  try {
    return localStorage.getItem(PROJECT_STORAGE);
  } catch {
    return null;
  }
}

export function saveSelectedProjectId(id: string) {
  localStorage.setItem(PROJECT_STORAGE, id);
}

export function formatFaNumber(value: number): string {
  return new Intl.NumberFormat('fa-IR').format(value);
}

export {
  formatRelativeFa,
  formatTime,
  parseUtcDate,
  toUtcIso,
} from './datetime';

const FALLBACK_ORIGIN = 'https://example.com';

export type ProjectUrlHints = {
  siteUrl?: string | null;
  sitemapUrl?: string | null;
  indexNowDomain?: string | null;
};

/** When project lacks siteUrl (e.g. early onboarding), use selected domain name. */
export function projectUrlHintsWithDomain(
  project: ProjectUrlHints | null | undefined,
  domainName?: string | null,
): ProjectUrlHints {
  const fallbackSiteUrl = siteUrlFromDomainName(domainName);
  const siteUrl = project?.siteUrl?.trim() || fallbackSiteUrl || null;
  const fallbackOrigin =
    siteUrl?.replace(/\/$/, '') || fallbackSiteUrl?.replace(/\/$/, '');
  return {
    siteUrl,
    sitemapUrl: project?.sitemapUrl ?? null,
    indexNowDomain: project?.indexNowDomain?.trim() || fallbackOrigin || null,
  };
}

function originFromUrl(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  try {
    const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return new URL(href).origin;
  } catch {
    return null;
  }
}

export function projectExampleOrigin(
  project: ProjectUrlHints | null | undefined,
): string {
  for (const candidate of [
    project?.siteUrl,
    project?.indexNowDomain,
    project?.sitemapUrl,
  ]) {
    const origin = originFromUrl(candidate);
    if (origin) return origin;
  }
  return FALLBACK_ORIGIN;
}

export function projectUrlListPlaceholder(
  project: ProjectUrlHints | null | undefined,
): string {
  const origin = projectExampleOrigin(project);
  return `${origin}/page-1\n${origin}/page-2`;
}

export function projectUrlPlaceholder(
  project: ProjectUrlHints | null | undefined,
  path = 'page',
): string {
  return `${projectExampleOrigin(project)}/${path}`;
}

export function projectSitemapPlaceholder(
  project: ProjectUrlHints | null | undefined,
): string {
  if (project?.sitemapUrl?.trim()) return project.sitemapUrl.trim();
  return `${projectExampleOrigin(project)}/sitemap.xml`;
}

export function projectSitemapListPlaceholder(
  project: ProjectUrlHints | null | undefined,
): string {
  const primary = projectSitemapPlaceholder(project);
  const secondary = `${projectExampleOrigin(project)}/news-sitemap.xml`;
  if (primary === secondary) return primary;
  return `${primary}\n${secondary}`;
}

export function hostFromProjectUrl(
  raw: string | null | undefined,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  const scMatch = trimmed.match(/^sc-domain:(.+)$/i);
  if (scMatch) {
    const host = scMatch[1].trim().toLowerCase();
    return host || null;
  }

  try {
    const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return new URL(href).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function resolveProjectHost(
  project: ProjectUrlHints | null | undefined,
): string | null {
  if (!project) return null;
  for (const candidate of [
    project.siteUrl,
    project.indexNowDomain,
    project.sitemapUrl,
  ]) {
    const host = hostFromProjectUrl(candidate);
    if (host) return host;
  }
  return null;
}

export function urlMatchesProjectHost(
  url: string,
  projectHost: string,
): boolean {
  try {
    return new URL(url).hostname.toLowerCase() === projectHost.toLowerCase();
  } catch {
    return false;
  }
}

export function partitionUrlsForProjectDomain(
  urls: string[],
  project: ProjectUrlHints | null | undefined,
): {
  valid: string[];
  invalid: string[];
  wrongDomain: string[];
  projectHost: string | null;
} {
  const projectHost = resolveProjectHost(project);
  const invalid: string[] = [];
  const formatValid: string[] = [];

  for (const url of urls) {
    try {
      new URL(url);
      formatValid.push(url);
    } catch {
      invalid.push(url);
    }
  }

  if (!projectHost) {
    return {
      valid: formatValid,
      invalid,
      wrongDomain: [],
      projectHost: null,
    };
  }

  const valid: string[] = [];
  const wrongDomain: string[] = [];
  for (const url of formatValid) {
    if (urlMatchesProjectHost(url, projectHost)) {
      valid.push(url);
    } else {
      wrongDomain.push(url);
    }
  }

  return { valid, invalid, wrongDomain, projectHost };
}

export function validateProjectUrlList(
  urls: string[],
  project: ProjectUrlHints | null | undefined,
  limit: number,
): string | null {
  if (urls.length === 0) return 'حداقل یک URL وارد کنید.';
  if (urls.length > limit) {
    return `حداکثر ${limit} آدرس در هر درخواست مجاز است.`;
  }

  const { invalid, wrongDomain, projectHost } = partitionUrlsForProjectDomain(
    urls,
    project,
  );

  if (!projectHost) {
    return 'دامنه پروژه تنظیم نشده است — از ادمین بخواهید siteUrl یا sitemap را تنظیم کند.';
  }

  if (invalid.length > 0) {
    return `آدرس نامعتبر: ${invalid.slice(0, 3).join('، ')}`;
  }

  if (wrongDomain.length > 0) {
    const sample = wrongDomain.slice(0, 3).join('، ');
    return `این آدرس‌ها متعلق به دامنه ${projectHost} نیستند: ${sample}`;
  }

  return null;
}
